import { useEffect, useState } from "react";
import { Loader2, Share2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * ReferLeadsDialog — hand over one or many leads to a teammate.
 * Props:
 *  - open, onOpenChange
 *  - leadIds: string[]   (1 = single refer, >1 = bulk refer)
 *  - currentUserId: string (hide self from picker)
 *  - onDone: (count) => void  (parent refetches)
 */
export default function ReferLeadsDialog({
  open,
  onOpenChange,
  leadIds = [],
  currentUserId,
  toCounseling = false,
  onDone,
}) {
  const [team, setTeam] = useState([]);
  const [toUser, setToUser] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setToUser("");
    setNote("");
    setLoading(true);
    api
      .get("/admin/counsellors")
      .then(({ data }) => {
        const list = (data?.counsellors || []).filter(
          (c) => c.id !== currentUserId,
        );
        setTeam(list);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, currentUserId]);

  const submit = async () => {
    if (!toUser) {
      toast.error("Pick a team member to refer to.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/leads/refer", {
        lead_ids: leadIds,
        to_user_id: toUser,
        note: note.trim() || undefined,
        to_counseling: toCounseling,
      });
      toast.success(
        `Referred ${data.referred} lead${data.referred === 1 ? "" : "s"} to ${data.to_name}`,
      );
      onOpenChange(false);
      onDone?.(data.referred);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="refer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-[#0A2540]" />
            Refer {leadIds.length} lead{leadIds.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>
            {toCounseling ? (
              <>
                Send these leads to a counsellor. They become the new owner and
                the leads move into the <strong>Counselings</strong> pipeline.
              </>
            ) : (
              <>
                Hand these leads over to a teammate. They become the new owner.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">
              Refer to
            </label>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading team…
              </div>
            ) : (
              <Select value={toUser} onValueChange={setToUser}>
                <SelectTrigger data-testid="refer-teammate-select">
                  <SelectValue placeholder="Select a team member" />
                </SelectTrigger>
                <SelectContent>
                  {team.length === 0 ? (
                    <div className="px-2 py-3 text-center text-xs text-slate-400">
                      No other team members
                    </div>
                  ) : (
                    team.map((c) => (
                      <SelectItem
                        key={c.id}
                        value={c.id}
                        data-testid={`refer-option-${c.id}`}
                      >
                        {c.name}{" "}
                        <span className="text-[10px] uppercase text-slate-400">
                          · {c.role}
                        </span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">
              Note (optional)
            </label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why are you referring this lead?"
              rows={3}
              data-testid="refer-note"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="refer-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !toUser}
            data-testid="refer-submit"
            className="bg-[#0A2540] hover:bg-[#0A2540]/90"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Refer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
